import { useState } from 'react';
import { Share2, Link2, MessageCircle, ExternalLink, Globe, Instagram, Facebook, Copy, Info } from 'lucide-react';
import { toast } from 'sonner';
import { copiarTexto } from '../data/produtos';
import { useLogo } from '../hooks/useLogo';

export default function Compartilhar() {
  const logo = useLogo();
  const link = typeof window !== 'undefined' ? window.location.origin : '';
  const ehPreview = link.includes('preview');
  const [copiado, setCopiado] = useState('');

  const mensagem = `Olá! Confira o catálogo digital da MK Eletromóveis e conheça nossos produtos e ofertas. Acesse pelo link: ${link}`;
  const mensagemSocial = `MK Eletromóveis — Catálogo Digital! Geladeiras, Smart TVs, fogões, móveis e muito mais, com ofertas e atendimento direto pelo WhatsApp. Confira: ${link}`;

  const copiar = async (texto, rotulo, chave) => {
    const ok = await copiarTexto(texto);
    if (ok) {
      setCopiado(chave);
      toast.success(`${rotulo} copiado!`);
      setTimeout(() => setCopiado(''), 2000);
    } else {
      toast.error('Não foi possível copiar. Selecione o endereço e copie manualmente.');
    }
  };

  const compartilharNativo = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'MK Eletromóveis — Catálogo Digital', text: mensagem, url: link });
      } catch {
        /* usuário cancelou */
      }
    } else {
      const ok = await copiarTexto(mensagem);
      if (ok) toast.success('Este dispositivo não tem compartilhamento direto. Link e mensagem copiados — cole no aplicativo que preferir.');
      else toast.error('Copie o link manualmente no campo acima.');
    }
  };

  const wa = `https://wa.me/?text=${encodeURIComponent(mensagem)}`;
  const fb = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`;

  const btnBase = 'inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-bold transition-colors disabled:opacity-60';

  return (
    <div data-testid="admin-compartilhar" className="max-w-2xl">
      <h1 className="flex items-center gap-2 font-heading text-2xl font-extrabold text-slate-900">
        <Share2 size={23} className="text-mk-blue" /> Compartilhar catálogo
      </h1>
      <p className="mt-1 text-sm text-slate-500">Divulgue o catálogo completo para clientes e nas redes sociais.</p>

      {ehPreview && (
        <p className="mt-4 flex items-start gap-2 rounded-2xl bg-amber-50 px-4 py-3 text-xs font-semibold text-amber-800">
          <Info size={15} className="mt-0.5 shrink-0" />
          Este é o endereço de demonstração (preview). Depois de publicar o site com o botão Deploy, esta página passa a usar automaticamente o link oficial — sem precisar alterar nada.
        </p>
      )}

      <div className="mt-4 rounded-2xl bg-white p-5 shadow-sm">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Endereço público do catálogo</p>
        <div className="mt-2 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <Globe size={16} className="shrink-0 text-mk-blue" />
          <span data-testid="share-link-text" className="min-w-0 flex-1 truncate text-sm font-bold text-slate-800">{link}</span>
        </div>
        <div className="mt-4 grid gap-2.5 sm:grid-cols-2">
          <button data-testid="share-copy-link" onClick={() => copiar(link, 'Link', 'link')} className={`${btnBase} border-2 border-mk-blue text-mk-blue hover:bg-blue-50`}>
            <Link2 size={16} /> {copiado === 'link' ? 'Copiado!' : 'Copiar link'}
          </button>
          <a data-testid="share-whatsapp" href={wa} target="_blank" rel="noopener noreferrer" className={`${btnBase} bg-mk-green text-white shadow-md shadow-mk-green/30 hover:bg-mk-greendark`}>
            <MessageCircle size={16} /> Compartilhar pelo WhatsApp
          </a>
          <button data-testid="share-native" onClick={compartilharNativo} className={`${btnBase} bg-mk-blue text-white hover:bg-mk-bluelight`}>
            <Share2 size={16} /> Compartilhar (apps do dispositivo)
          </button>
          <a data-testid="share-open" href="/" target="_blank" rel="noopener noreferrer" className={`${btnBase} border-2 border-slate-300 text-slate-600 hover:bg-slate-50`}>
            <ExternalLink size={16} /> Abrir catálogo
          </a>
        </div>
      </div>

      <div className="mt-4 rounded-2xl bg-white p-5 shadow-sm">
        <h2 className="font-heading text-base font-extrabold text-slate-900">Divulgar catálogo nas redes sociais</h2>
        <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
          <a data-testid="share-wa-status" href={wa} target="_blank" rel="noopener noreferrer" className={`${btnBase} border-2 border-mk-green text-mk-greendark hover:bg-mk-greensoft`}>
            <MessageCircle size={16} /> WhatsApp e Status
          </a>
          <a data-testid="share-facebook" href={fb} target="_blank" rel="noopener noreferrer" className={`${btnBase} border-2 border-[#1877F2] text-[#1877F2] hover:bg-blue-50`}>
            <Facebook size={16} /> Facebook
          </a>
          <button
            data-testid="share-instagram"
            onClick={async () => {
              await copiar(mensagemSocial, 'Mensagem para o Instagram', 'insta');
              window.open('https://www.instagram.com', '_blank', 'noopener,noreferrer');
              toast.info('O Instagram não aceita links por compartilhamento direto. A mensagem foi copiada — cole no seu post, story ou bio.');
            }}
            className={`${btnBase} border-2 border-[#C13584] text-[#C13584] hover:bg-pink-50`}
          >
            <Instagram size={16} /> Instagram (copiar mensagem)
          </button>
          <button data-testid="share-copy-msg" onClick={() => copiar(mensagemSocial, 'Mensagem de divulgação', 'msg')} className={`${btnBase} border-2 border-slate-300 text-slate-600 hover:bg-slate-50`}>
            <Copy size={16} /> {copiado === 'msg' ? 'Copiada!' : 'Copiar mensagem completa'}
          </button>
        </div>
        <p className="mt-3 text-xs text-slate-400">
          WhatsApp e Facebook abrem com a mensagem pronta. Instagram e outras redes não aceitam link automático — a mensagem é copiada para você colar.
        </p>
      </div>

      <div className="mt-4 rounded-2xl bg-white p-5 shadow-sm">
        <h2 className="font-heading text-base font-extrabold text-slate-900">Prévia do compartilhamento</h2>
        <p className="mt-1 text-xs text-slate-400">É assim que o link tende a aparecer no WhatsApp, Facebook e outras redes.</p>
        <div data-testid="share-preview" className="mt-3 max-w-md overflow-hidden rounded-2xl border border-slate-200">
          <div className="flex h-32 items-center justify-center bg-mk-blue/5 p-4">
            <img src={logo} alt="MK Eletromóveis" className="max-h-24 max-w-[70%] object-contain" />
          </div>
          <div className="p-3.5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{link.replace(/^https?:\/\//, '')}</p>
            <p className="mt-0.5 text-sm font-extrabold text-slate-900">MK Eletromóveis — Catálogo Digital</p>
            <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
              Confira nossos produtos e ofertas: geladeiras, Smart TVs, fogões, móveis e muito mais. Atendimento direto pelo WhatsApp.
            </p>
          </div>
        </div>
        <p className="mt-3 text-xs text-slate-400">
          Título e descrição já configurados no código. A imagem de prévia nas redes será ativada com o endereço oficial após o Deploy (as redes não aceitam imagem de endereço temporário).
        </p>
      </div>
    </div>
  );
}
